// ABOUTME: The question a delete of a calendar item asks, wherever it started: any code asks through askDeleteItem,
// ABOUTME: and one dialog for the app (DeleteItemQuestion, mounted in AppShell) shows it, locked while the delete runs.
import { useCallback, useEffect, useSyncExternalStore } from "react"
import { useApolloClient } from "@apollo/client/react"
import { useErrorNotifier } from "@/app/providers/useErrorNotifier"
import { createLogger } from "@/model/logger"
import { deleteItemWithDiscussion } from "./itemDelete"

const logger = createLogger("Discussion")

interface QuestionState {
  /** Messages that go with the item, 0 for the plain question; null while nothing is asked. */
  messageCount: number | null
  /** The delete the person agreed to is on its way: the dialog stays up, its buttons locked. */
  isDeleting: boolean
}

let state: QuestionState = { messageCount: null, isDeleting: false }
let pending: { resolve: (yes: boolean) => void; deletesOnYes: boolean } | null = null
let hosts = 0
const listeners = new Set<() => void>()

const update = (next: Partial<QuestionState>) => {
  state = { ...state, ...next }
  listeners.forEach((listener) => listener())
}

/** Answers the open question; a yes whose delete follows at once keeps the dialog up, locked, in the same change. */
const settle = (yes: boolean) => {
  const asked = pending
  if (!asked) return
  pending = null
  update({ messageCount: null, isDeleting: yes && asked.deletesOnYes })
  asked.resolve(yes)
}

export interface AskOptions {
  /** The delete follows a yes at once: the dialog stays up, locked, until whileDeleting ends — no flicker between. */
  deletesOnYes?: boolean
}

/**
 * Asks the person to delete an item with `messageCount` messages, 0 for the plain question. A question still open is
 * answered no — one at a time. A card asks through here rather than a dialog of its own: the card is hidden for the
 * undo, and a refusal of the delete after it asks again (useUndoableCalendarItemDelete). Nothing mounted to ask
 * with: the item stays.
 */
export function askDeleteItem(messageCount: number, { deletesOnYes = false }: AskOptions = {}): Promise<boolean> {
  if (hosts === 0) {
    logger.warn("No dialog to ask about a delete with; the item stays")
    return Promise.resolve(false)
  }
  settle(false)
  return new Promise((resolve) => {
    pending = { resolve, deletesOnYes }
    update({ messageCount })
  })
}

/** Keeps the dialog up and locked for as long as the delete the person agreed to takes. */
export async function whileDeleting<T>(work: () => Promise<T>): Promise<T> {
  update({ isDeleting: true })
  try {
    return await work()
  } finally {
    update({ isDeleting: false })
  }
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
const snapshot = () => state

/**
 * What the app's dialog shows, and its answer. Mounted once, for the whole app (DeleteItemQuestion); leaving with
 * a question open answers it no — its delete waits for nothing.
 */
export function useDeleteQuestion(): QuestionState & { answer: (yes: boolean) => void } {
  const current = useSyncExternalStore(subscribe, snapshot)
  useEffect(() => {
    hosts += 1
    return () => {
      hosts -= 1
      if (hosts === 0) settle(false)
    }
  }, [])
  return { ...current, answer: settle }
}

/**
 * A window's delete of its calendar item: the question in the app's dialog — the plain one, or how many messages go
 * with it — and the delete under its lock, the window open until the server answers (§3.9). `remove` rejects with the
 * server's refusal; a refusal because someone wrote since asks again. Failures are shown, a refusal at the number
 * agreed to as the account's (ItemDeleteBlocked); `onDeleted` once the item is gone.
 */
export function useDeleteItemAsking() {
  const client = useApolloClient()
  const { notifyError } = useErrorNotifier()
  return useCallback(
    (itemId: string, remove: (confirmedThroughSeq: number) => Promise<unknown>, onDeleted: () => void) => {
      deleteItemWithDiscussion({
        client,
        itemId,
        askAlways: true,
        ask: (messageCount) => askDeleteItem(messageCount, { deletesOnYes: true }),
        remove: (confirmed) => whileDeleting(() => remove(confirmed)),
      })
        .then((outcome) => {
          if (outcome === "deleted") onDeleted()
        })
        .catch(notifyError)
    },
    [client, notifyError],
  )
}

/** Test seam — the module's question outlives a test otherwise. */
export function resetDeleteQuestionForTests(): void {
  // Not the hosts: a test's dialog is unmounted after this, and counts itself out then.
  pending = null
  state = { messageCount: null, isDeleting: false }
}
